import React, { useMemo } from "react";
import {
  AttendeeCheckinModal as GenericCheckinModal,
  type CheckinAttendee,
  type CheckinConfig,
  type AddAttendeeForm,
} from "newui/components/AttendeeCheckin";
import { useMeeting } from "../../hooks/useMeeting";
import { useMeetingAttendees } from "../../hooks/useMeetingAttendees";

interface MeetingCheckinModalProps {
  rfxId: number;
  evalId: number;
  isOpen: boolean;
  onClose: () => void;
  onMeetingStarted: () => void;
}

const MEETING_CHECKIN_CONFIG: CheckinConfig = {
  title: "Attendee Check-in",
  continueLabel: "Continue to Meeting",
  addLabel: "Add Late Attendee",
  addFlags: [{ key: "is_panel_member", label: "Panel member", default: false }],
  iconType: "info",
};

export const AttendeeCheckinModal: React.FC<MeetingCheckinModalProps> = ({
  rfxId,
  evalId,
  isOpen,
  onClose,
  onMeetingStarted,
}) => {
  const { startMeeting } = useMeeting(rfxId, evalId);
  const { attendees, isLoading, updateAttendee, addAttendee } =
    useMeetingAttendees(rfxId, evalId);

  const checkinAttendees: CheckinAttendee[] = useMemo(
    () =>
      attendees.map((a) => ({
        id: a.id,
        name: a.name,
        email: a.email,
        attendance_status: a.attendance_status,
        comment: a.comment,
        badges: a.is_panel_member ? ["Panel Member"] : undefined,
      })),
    [attendees]
  );

  const handleStatusChange = (
    attendeeId: number,
    status: "attending" | "not_attending",
    comment?: string
  ) => {
    updateAttendee.mutate({
      attendeeId,
      payload: { attendance_status: status, comment },
    });
  };

  const handleCommentSave = (
    attendeeId: number,
    status: "attending" | "not_attending",
    comment: string
  ) => {
    updateAttendee.mutate({
      attendeeId,
      payload: { attendance_status: status, comment: comment || undefined },
    });
  };

  const handleAddAttendee = (form: AddAttendeeForm) => {
    addAttendee.mutate({
      name: form.name,
      email: form.email,
      is_panel_member: form.flags?.is_panel_member ?? false,
    });
  };

  const handleContinue = () => {
    startMeeting.mutate(undefined, {
      onSuccess: () => {
        onClose();
        onMeetingStarted();
      },
    });
  };

  return (
    <GenericCheckinModal
      isOpen={isOpen}
      onClose={onClose}
      attendees={checkinAttendees}
      isLoading={isLoading}
      config={MEETING_CHECKIN_CONFIG}
      isContinuing={startMeeting.isPending}
      isAdding={addAttendee.isPending}
      onContinue={handleContinue}
      onStatusChange={handleStatusChange}
      onCommentSave={handleCommentSave}
      onAddAttendee={handleAddAttendee}
    />
  );
};
